import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Router, NavigationStart } from '@angular/router';
import { WorkScheduleBulkDataService, BulkContractorCompany, BulkProjectOption } from '../../../services/utils/work-schedule-bulk-data.service';
import { SnackBarService } from '../../../services/utils/snackBar.service';
import { DialogService } from '../../../services/utils/dialog.service';
import { AuthGuardService } from '../../../services/guards/AuthGuardService';
import { DialogRepeatPatternData } from '../dialog-repeat-pattern/dialog-repeat-pattern.component';
import { describeScheduleErrorHint } from '../../../utils/work-schedule-error.util';
import { dateKey } from '../../../utils/work-schedule-calendar.util';
import { formatApiTimeRange, toShortTime12h } from '../../../utils/time-format.util';
import {
  applyPatternToRowDays,
  BulkPersonRow,
  BulkRotationCard,
  BulkSaveItemApi,
  BulkSaveResultApi,
  buildWeekDays,
  clearRowDay,
  computePersonWeekTotal,
  formatWeekRangeLabel,
  lightenColor,
  LETTER_COLORS,
  LETTER_LABELS,
  MappedBulkSchedule,
  ShiftPattern,
  WeekDay
} from '../../../utils/work-schedule-bulk.util';

export interface DialogWorkScheduleBulkData {
  projectId: string;
  companyInfoId: string;
  projectName: string;
  stageName: string;
  employers: any[];
}

const PAGE_SIZE = 50;

function startOfWeek(date: Date): Date {
  const result = new Date(date);
  const isoWeekday = (result.getDay() + 6) % 7; // 0=Lunes..6=Domingo
  result.setDate(result.getDate() - isoWeekday);
  result.setHours(0, 0, 0, 0);
  return result;
}

@Component({
  selector: 'app-dialog-work-schedule-bulk',
  templateUrl: './dialog-work-schedule-bulk.component.html',
  styleUrls: ['./dialog-work-schedule-bulk.component.scss']
})
export class DialogWorkScheduleBulkComponent implements OnInit, OnDestroy {
  /** mat-select trata null/undefined como "sin selección" aunque exista una mat-option con ese
   * valor, así que el combo "Empresa" usa este centinela para representar "Todas las empresas"
   * en la UI — selectedCompanyId sigue siendo null internamente (filtro sin aplicar). */
  readonly ALL_COMPANIES = '__ALL_COMPANIES__';

  loading = true;
  scheduleLoading = false;
  saving = false;
  canSave: boolean;
  renderError: string | null = null;

  weekStartDate: Date = startOfWeek(new Date());
  weekDays: WeekDay[] = [];
  patterns: ShiftPattern[] = [];
  /** Patrones catorcenales (rotaciones) del proyecto: solo informativos en esta pantalla. */
  rotationCards: BulkRotationCard[] = [];
  maxWeeklyHours = 0;

  /** Primer día editable devuelto por el servidor; null = sin tope. */
  editableFrom: Date | null = null;

  page = 1;
  pageSize = PAGE_SIZE;
  totalPeople = 0;
  totalPages = 1;

  companies: BulkContractorCompany[] = [];
  selectedCompanyId: string | null = null;

  permissions: string[] = [];

  /** Proyectos de la empresa para el combo "Nombre del proyecto" del encabezado. */
  availableProjects: BulkProjectOption[] = [];

  personnel: BulkPersonRow[] = [];
  selectedPersonIds = new Set<string>();
  searchText = '';

  /** true = hay ediciones locales (soltar un turno/patrón sobre una celda o columna) sin
   * guardar todavía. "Aplicar" (repetir patrón) y "Limpiar" ya guardan directo al soltar, así
   * que nunca dejan esto en true. Evita que "Guardar" reenvíe la semana visible sin ningún
   * cambio real: como el backend no acota el borrado al `to` que se le manda (ver
   * api-programacion-horarios.md §9, "lo que va después del rango guardado queda sin
   * programar"), un guardado redundante de una sola semana puede borrar semanas futuras que
   * ya estaban guardadas por un "Aplicar" anterior — justamente lo que reportó el usuario. */
  hasLocalDraftChanges = false;

  /** Resumen de la última vez que se guardó (Requested/Applied/Partial/Skipped + detalle por persona). */
  saveResult: BulkSaveResultApi | null = null;

  /** personId → dateKey → motivo, construido a partir del último `saveResult` — pinta en rojo
   * exactamente el día que el backend rechazó (ej. horas extra sin el permiso correspondiente),
   * en vez de dejar el motivo escondido solo en el panel de resumen. Se limpia con cada intento
   * de guardado nuevo. */
  private rejectedCellReasons = new Map<string, Map<string, string>>();

  private routerSub: Subscription;

  draggedPatternId: string | null = null;
  tapArmedPatternId: string | null = null;
  dragOverTarget: string | null = null;

  readonly letterLabels = LETTER_LABELS;

  constructor(
    public dialogRef: MatDialogRef<DialogWorkScheduleBulkComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogWorkScheduleBulkData,
    private dataService: WorkScheduleBulkDataService,
    private snackBService: SnackBarService,
    private dialogService: DialogService,
    private authService: AuthGuardService,
    private router: Router
  ) {
    this.permissions = this.authService.auth.getUser().claims;
    this.canSave = this.exitPermission('schedule.bulk');
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => {
      setTimeout(() => this.dialogRef.close(), 0);
    });
  }

  exitPermission(permiso: string): boolean {
    return this.permissions.find(x => x === permiso) !== undefined;
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.weekDays = buildWeekDays(this.weekStartDate);
    this.dataService.getProjectsForCompany(this.data.companyInfoId).subscribe({
      next: (projects) => { this.availableProjects = projects; },
      error: () => { this.availableProjects = []; }
    });
    // Mismas empresas para todos los proyectos de esta empresa administradora — no depende
    // del proyecto en edición, así que se carga una sola vez. La carga inicial de personal
    // espera esta respuesta para arrancar ya filtrada por la primera empresa (en vez de
    // "Todas las empresas"), que trae mucho menos personal y carga más rápido.
    this.dataService.getCompanyEmployers(this.data.companyInfoId).subscribe({
      next: (employers) => {
        this.companies = employers;
        this.selectedCompanyId = employers[0]?.employerId || null;
        this.loadInitialData();
      },
      error: () => {
        this.companies = [];
        this.loadInitialData();
      }
    });
  }

  /** Combo "Nombre del proyecto" del encabezado — cambia el proyecto en edición sin cerrar el diálogo. */
  selectProject(projectId: string): void {
    if (this.scheduleLoading || !projectId || projectId === this.data.projectId) { return; }
    const project = this.availableProjects.find(p => p.id === projectId);
    this.data.projectId = projectId;
    this.data.projectName = project?.name || this.data.projectName;
    this.data.stageName = '';
    this.selectedCompanyId = null;
    this.page = 1;
    this.selectedPersonIds.clear();
    this.saveResult = null;
    this.loadProjectWorkShifts(projectId);
    this.reloadPersonnel();
  }

  private loadProjectWorkShifts(projectId: string): void {
    this.dataService.getProjectWorkShifts(projectId).subscribe({
      next: (patterns) => {
        this.patterns = patterns;
        this.loadProjectRotations(projectId, patterns);
      },
      error: () => {
        this.patterns = [];
        this.rotationCards = [];
      }
    });
  }

  private loadProjectRotations(projectId: string, patterns: ShiftPattern[]): void {
    this.dataService.getProjectRotations(projectId, patterns).subscribe({
      next: (cards) => { this.rotationCards = cards; },
      error: () => { this.rotationCards = []; }
    });
  }

  private loadInitialData(): void {
    this.loading = true;
    this.renderError = null;
    this.loadProjectWorkShifts(this.data.projectId);
    forkJoin({
      schedule: this.fetchScheduleObservable(),
      maxWeeklyHours: this.dataService.getMaxWeeklyHours(this.data.companyInfoId)
    }).subscribe({
      next: ({ schedule, maxWeeklyHours }) => {
        try {
          this.applyMappedSchedule(schedule);
          this.maxWeeklyHours = maxWeeklyHours;
        } catch (error) {
          this.reportRenderError('loadInitialData', error);
        } finally {
          this.loading = false;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.snackBService.openError(this.extractErrorMessage(err));
        this.reportRenderError('carga de datos (turnos/personal/jornada máxima)', err);
      }
    });
  }

  private fetchScheduleObservable(): Observable<MappedBulkSchedule> {
    const { from, to } = this.weekRange();
    return this.dataService.getScheduleBulk(this.data.projectId, from, to, this.selectedCompanyId, this.page, this.pageSize);
  }

  private applyMappedSchedule(mapped: MappedBulkSchedule): void {
    this.personnel = mapped.personnel;
    this.editableFrom = mapped.editableFrom;
    this.page = mapped.page;
    this.pageSize = mapped.pageSize;
    this.totalPeople = mapped.totalPeople;
    this.totalPages = Math.max(1, mapped.totalPages);
    // Lo que se acaba de cargar es la verdad del servidor — cualquier borrador local anterior
    // (de esta semana/página) ya no aplica.
    this.hasLocalDraftChanges = false;
  }

  private reloadPersonnel(): void {
    this.scheduleLoading = true;
    this.fetchScheduleObservable().subscribe({
      next: (mapped) => {
        this.scheduleLoading = false;
        try {
          this.applyMappedSchedule(mapped);
        } catch (error) {
          this.reportRenderError('reloadPersonnel', error);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.scheduleLoading = false;
        this.snackBService.openError(this.extractErrorMessage(err));
      }
    });
  }

  private weekRange(): { from: string; to: string } {
    return { from: dateKey(this.weekDays[0].date), to: dateKey(this.weekDays[6].date) };
  }

  /** Recorta `from` a editableFrom si la semana empieza antes del tope — evita que el POST
   * completo rebote con PAST_DAYS_NOT_ALLOWED por días bloqueados (mismo ajuste que en el
   * calendario individual). Null si toda la semana cae antes del tope. */
  private editableWeekRange(): { from: string; to: string } | null {
    const weekStart = this.weekDays[0].date;
    const weekEnd = this.weekDays[6].date;
    const editableFrom = this.effectiveEditableFrom();
    const from = editableFrom && editableFrom > weekStart ? editableFrom : weekStart;
    if (from > weekEnd) { return null; }
    return { from: dateKey(from), to: dateKey(weekEnd) };
  }

  private reportRenderError(context: string, error: any): void {
    // tslint:disable-next-line:no-console
    console.error(`[work-schedule-bulk] ${context}`, error);
    if (!this.renderError) {
      this.renderError = `Error en "${context}": ${error?.message || error?.statusText || JSON.stringify(error)}`;
    }
  }

  /** Cubre los 3 formatos de error del doc: 409 negocio (camelCase), resto (PascalCase `Error`). */
  private extractErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 409 && err.error?.message) {
      return err.error.message + describeScheduleErrorHint(err.error.code);
    }
    return err.error?.Error || err.error?.Message || 'Ocurrió un error al procesar la programación';
  }

  /** Mensaje por persona en el resumen de guardado — igual que extractErrorMessage, aclara con
   * qué permiso se resolvería un `Reason` de horas extra en vez de mostrar solo el código crudo. */
  describeItemMessage(item: BulkSaveItemApi): string {
    const hint = describeScheduleErrorHint(item.Reason);
    return hint ? `${item.Message || ''}${hint}` : (item.Message || '');
  }

  /** El badge de estado por persona venía en inglés tal cual lo manda la API. Traduce a los
   * mismos términos que ya usa el resumen de arriba ("completos"/"parciales"/"sin cambios"). */
  describeStatus(status: string): string {
    if (status === 'APPLIED') { return 'COMPLETO'; }
    if (status === 'PARTIAL') { return 'PARCIAL'; }
    if (status === 'SKIPPED') { return 'SIN CAMBIOS'; }
    return status;
  }

  get subtitle(): string {
    return `${this.data.stageName || this.data.projectName} · semana ${this.weekRangeLabel}`;
  }

  get weekRangeLabel(): string {
    return formatWeekRangeLabel(this.weekStartDate);
  }

  get filteredPersonnel(): BulkPersonRow[] {
    try {
      const search = this.searchText.trim().toLowerCase();
      return this.personnel.filter(row => {
        const matchesSearch = !search
          || (row.fullName || '').toLowerCase().includes(search)
          || (row.documentNumber || '').includes(search);
        return matchesSearch;
      });
    } catch (error) {
      this.reportRenderError('filteredPersonnel', error);
      return [];
    }
  }

  get overLimitCount(): number {
    return this.filteredPersonnel.filter(row => computePersonWeekTotal(row, this.maxWeeklyHours).exceeds).length;
  }

  goPrevWeek(): void {
    this.changeWeek(-7);
  }

  goNextWeek(): void {
    this.changeWeek(7);
  }

  private changeWeek(offsetDays: number): void {
    if (this.scheduleLoading) { return; }
    const nextStart = new Date(this.weekStartDate);
    nextStart.setDate(nextStart.getDate() + offsetDays);
    this.weekStartDate = nextStart;
    this.weekDays = buildWeekDays(this.weekStartDate);
    this.page = 1;
    this.selectedPersonIds.clear();
    this.reloadPersonnel();
  }

  goPrevPage(): void {
    if (this.scheduleLoading || this.page <= 1) { return; }
    this.page--;
    this.reloadPersonnel();
  }

  goNextPage(): void {
    if (this.scheduleLoading || this.page >= this.totalPages) { return; }
    this.page++;
    this.reloadPersonnel();
  }

  goToPage(page: number | '...'): void {
    if (page === '...' || this.scheduleLoading || page === this.page) { return; }
    this.page = page;
    this.reloadPersonnel();
  }

  isCurrentPage(page: number | '...'): boolean {
    return page !== '...' && page === this.page;
  }

  /** Igual al paginador de dialog-generate-withdrawals-report: siempre muestra primera/última
   * página, hasta 3 páginas alrededor de la actual, y "..." para el resto. */
  get visiblePages(): (number | '...')[] {
    const total = this.totalPages;
    const current = this.page;
    const pages: (number | '...')[] = [];

    if (total <= 7) {
      for (let p = 1; p <= total; p++) { pages.push(p); }
      return pages;
    }

    pages.push(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) { pages.push('...'); }
    for (let p = start; p <= end; p++) { pages.push(p); }
    if (end < total - 1) { pages.push('...'); }
    pages.push(total);
    return pages;
  }

  /** Combo "Empresa" del encabezado — selección única, null = "Todas las empresas". */
  selectCompany(employerId: string | null): void {
    if (this.scheduleLoading || this.selectedCompanyId === employerId) { return; }
    this.selectedCompanyId = employerId;
    this.page = 1;
    this.selectedPersonIds.clear();
    this.reloadPersonnel();
  }

  /** ngModel del combo "Empresa": traduce entre el centinela ALL_COMPANIES que usa la UI y el
   * selectedCompanyId (null = todas) que usa el resto del componente. */
  get companyFilterValue(): string {
    return this.selectedCompanyId || this.ALL_COMPANIES;
  }

  set companyFilterValue(value: string) {
    this.selectCompany(value === this.ALL_COMPANIES ? null : value);
  }

  get weeklyPatterns(): ShiftPattern[] {
    return this.patterns.filter(pattern => pattern.isActive && !pattern.isDaily);
  }

  get dailyShifts(): ShiftPattern[] {
    return this.patterns.filter(pattern => pattern.isActive && pattern.isDaily);
  }

  /** Chip de "TURNOS" en el sidebar: en vez del total de horas de la semana, muestra el horario
   * real (ej. "07:00 AM – 05:00 PM") tomado del primer día con jornada del turno — un turno diario
   * usa el mismo horario todos los días que trabaja, así que alcanza con el primero. */
  getShiftTimeRangeLabel(pattern: ShiftPattern): string {
    const scheduledDay = pattern.days.find(day => day.hasSchedule);
    if (!scheduledDay) { return ''; }
    return `${toShortTime12h(scheduledDay.startHour || '')} – ${toShortTime12h(scheduledDay.endHour || '')}`;
  }

  /** Seleccionar personas no tiene sentido si de todas formas no se puede guardar nada con
   * ellas — mismo permiso que ya gobierna arrastrar/tocar un patrón (canSave). */
  togglePerson(personId: string): void {
    if (!this.canSave) { return; }
    if (this.selectedPersonIds.has(personId)) {
      this.selectedPersonIds.delete(personId);
    } else {
      this.selectedPersonIds.add(personId);
    }
  }

  isPersonSelected(personId: string): boolean {
    return this.selectedPersonIds.has(personId);
  }

  get allFilteredSelected(): boolean {
    return this.filteredPersonnel.length > 0 && this.filteredPersonnel.every(row => this.selectedPersonIds.has(row.id));
  }

  toggleSelectAll(): void {
    if (!this.canSave) { return; }
    if (this.allFilteredSelected) {
      this.filteredPersonnel.forEach(row => this.selectedPersonIds.delete(row.id));
    } else {
      this.filteredPersonnel.forEach(row => this.selectedPersonIds.add(row.id));
    }
  }

  getPatternById(patternId: string): ShiftPattern | undefined {
    return this.patterns.find(pattern => pattern.id === patternId);
  }

  /** El backend tiene un bug conocido: no baja el tope de `EditableFrom` ni con
   * `schedule.edit-past` activo en los claims del usuario (confirmado con la respuesta real de
   * `Schedule/Bulk`). Mientras se corrige del lado del servidor, el front decide esto por su
   * cuenta: con `schedule.edit-past` se ignora el `EditableFrom` del servidor y no se bloquea
   * ningún día pasado. Es el único permiso que importa aquí — `projectworkshift.
   * RescheduleBeforeOTReview` se descartó: confirmado que el verdadero candado para reprogramar
   * un día ya transcurrido es que su prenómina esté "Sin revisar" (si no lo está, ningún permiso
   * lo destraba; hay que resetearlo desde Prenómina primero). */
  get canEditPastDays(): boolean {
    return this.exitPermission('schedule.edit-past');
  }

  /** `editableFrom` que realmente se usa para bloquear/recortar — null (sin tope) cuando
   * `canEditPastDays` es true, sin importar lo que haya devuelto el servidor. */
  private effectiveEditableFrom(): Date | null {
    return this.canEditPastDays ? null : this.editableFrom;
  }

  /** Día anterior a editableFrom — debe salir deshabilitado (ver calendario individual: si el
   * usuario lo edita igual, el POST respondería 409 PAST_DAYS_NOT_ALLOWED para todo el lote). */
  isDateLocked(date: Date): boolean {
    const editableFrom = this.effectiveEditableFrom();
    return !!editableFrom && date < editableFrom;
  }

  /** Día fuera de la vigencia del contrato de esta persona — la masiva no lo programa, lo
   * reportaría PARTIAL/SKIPPED, así que mejor deshabilitarlo antes de que el usuario lo intente. */
  isOutOfContract(row: BulkPersonRow, date: Date): boolean {
    return date < row.contractStart || (!!row.contractEnd && date > row.contractEnd);
  }

  isCellDisabled(row: BulkPersonRow, date: Date): boolean {
    return this.isDateLocked(date) || this.isOutOfContract(row, date);
  }

  /** Motivo del candado en una celda deshabilitada — prioriza schedule.edit-past porque es el
   * único permiso que ni un Administrador tiene por defecto (ver api-programacion-horarios.md). */
  getCellLockReason(row: BulkPersonRow, date: Date): string {
    if (this.isDateLocked(date)) { return 'No cuentas con el permiso para programar días ya transcurridos'; }
    if (this.isOutOfContract(row, date)) { return 'El contrato de esta persona no cubre esta fecha'; }
    return '';
  }

  isCellRescheduled(row: BulkPersonRow, date: Date): boolean {
    return row.rescheduledDates.has(dateKey(date));
  }

  /** true si el último guardado rechazó justo este día para esta persona (ej. horas extra sin
   * el permiso correspondiente) — independiente de isCellDisabled, que es sobre lo que se ve
   * ANTES de intentar guardar, no sobre lo que el backend terminó rechazando. */
  isCellRejected(row: BulkPersonRow, date: Date): boolean {
    return !!this.rejectedCellReasons.get(row.id)?.has(dateKey(date));
  }

  getCellRejectedReason(row: BulkPersonRow, date: Date): string {
    return this.rejectedCellReasons.get(row.id)?.get(dateKey(date)) || '';
  }

  /** Arma personId → dateKey → motivo a partir de `Items[].SkippedDates`/`Warnings` del
   * resultado del guardado, para poder pintar la celda exacta en rojo. */
  private buildRejectedCellReasons(result: BulkSaveResultApi): Map<string, Map<string, string>> {
    const map = new Map<string, Map<string, string>>();
    (result.Items || []).forEach(item => {
      if (item.Status === 'APPLIED') { return; }
      const personKey = String(item.PersonalEmployerId);
      const dayMap = map.get(personKey) || new Map<string, string>();

      const itemReasonText = (describeScheduleErrorHint(item.Reason).trim() || item.Message || 'No se pudo programar este día.');
      (item.SkippedDates || []).forEach(dateStr => {
        dayMap.set(dateKey(new Date(dateStr)), itemReasonText);
      });

      (item.Warnings || []).forEach(warning => {
        const warningReasonText = (describeScheduleErrorHint(warning.Code).trim() || warning.Message || 'No se pudo programar este día.');
        (warning.Dates || []).forEach(dateStr => {
          dayMap.set(dateKey(new Date(dateStr)), warningReasonText);
        });
      });

      if (dayMap.size > 0) { map.set(personKey, dayMap); }
    });
    return map;
  }

  getRowTotal(row: BulkPersonRow): { totalHours: number; exceeds: boolean } {
    try {
      return computePersonWeekTotal(row, this.maxWeeklyHours);
    } catch (error) {
      this.reportRenderError('getRowTotal', error);
      return { totalHours: 0, exceeds: false };
    }
  }

  /** El día viene del patrón catorcenal asignado (no es un ajuste manual): no se puede quitar ni
   * arrastrar; para cambiarlo se le suelta un turno encima. */
  isCellFromRotation(row: BulkPersonRow, day: WeekDay): boolean {
    return row.assignments[day.dayNumber]?.source === 'ROTATION';
  }

  getCellStyle(row: BulkPersonRow, day: WeekDay): { bg: string; color: string; label: string; timeRange: string } {
    try {
      const assignment = row.assignments[day.dayNumber];
      if (!assignment) { return { bg: 'transparent', color: '#c7ccd3', label: '', timeRange: '' }; }
      // Color/nombre real del turno/patrón asignado (el mismo color distintivo que ya se ve en
      // el sidebar) — antes se usaba un color por categoría de jornada (E/M/T/N/S/D) compartido
      // entre turnos distintos, así que cambiar de un turno diurno a otro se veía idéntico.
      const pattern = this.getPatternById(assignment.patternId);
      const patternDay = pattern?.days[day.dayNumber];
      // Un patrón semanal puede combinar días de turnos distintos (ej. Turno 8:00 el lunes,
      // Turno 8:30 el martes) — patternDay.color es el color real de ESE turno para ese día
      // concreto (igual que en "Configuración de horario de trabajo por proyecto" > Patrones);
      // pattern.color solo entra como respaldo cuando el día no coincide con ningún turno.
      const color = patternDay?.color || pattern?.color || LETTER_COLORS[assignment.letter];
      const bg = lightenColor(color, 0.86);
      const label = pattern?.name || LETTER_LABELS[assignment.letter];
      if (assignment.letter === 'D') { return { bg, color, label, timeRange: 'Sin jornada' }; }
      const timeRange = patternDay?.hasSchedule ? formatApiTimeRange(patternDay.startHour, patternDay.endHour) : '';
      return { bg, color, label, timeRange };
    } catch (error) {
      this.reportRenderError('getCellStyle', error);
      return { bg: 'transparent', color: '#c7ccd3', label: '', timeRange: '' };
    }
  }

  armPatternForTap(patternId: string): void {
    if (!this.canSave) { return; }
    this.tapArmedPatternId = this.tapArmedPatternId === patternId ? null : patternId;
  }

  onPatternDragStart(event: DragEvent, patternId: string): void {
    if (!this.canSave) { return; }
    this.draggedPatternId = patternId;
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', patternId);
      event.dataTransfer.effectAllowed = 'copy';
    }
  }

  onDragEnd(): void {
    this.draggedPatternId = null;
    this.dragOverTarget = null;
  }

  private resolveDraggedPatternId(event: DragEvent): string | null {
    return this.draggedPatternId || event.dataTransfer?.getData('text/plain') || null;
  }

  onDragOverTarget(event: DragEvent, targetKey: string): void {
    event.preventDefault();
    this.dragOverTarget = targetKey;
  }

  onDragLeaveTarget(): void {
    this.dragOverTarget = null;
  }

  isDragOver(targetKey: string): boolean {
    return this.dragOverTarget === targetKey;
  }

  applyPatternToCell(row: BulkPersonRow, day: WeekDay, patternId: string): void {
    const pattern = this.getPatternById(patternId);
    if (!pattern || this.isCellDisabled(row, day.date)) { return; }
    this.personnel = this.personnel.map(person =>
      person.id === row.id ? applyPatternToRowDays(person, pattern, [day.dayNumber]) : person);
    this.hasLocalDraftChanges = true;
    this.save();
  }

  applyPatternToColumn(day: WeekDay, patternId: string): void {
    const pattern = this.getPatternById(patternId);
    if (!pattern) { return; }
    if (this.selectedPersonIds.size === 0) {
      this.snackBService.openWarning('Selecciona al menos una persona antes de soltar un turno o patrón sobre un día.');
      return;
    }
    this.personnel = this.personnel.map(person =>
      this.selectedPersonIds.has(person.id) && !this.isCellDisabled(person, day.date)
        ? applyPatternToRowDays(person, pattern, [day.dayNumber])
        : person);
    this.hasLocalDraftChanges = true;
    this.save();
  }

  onDropPersonsHeader(event: DragEvent): void {
    event.preventDefault();
    const patternId = this.resolveDraggedPatternId(event);
    this.dragOverTarget = null;
    if (!patternId || !this.canSave) { return; }
    if (this.selectedPersonIds.size === 0) {
      this.snackBService.openWarning('Selecciona al menos una persona antes de soltar el patrón sobre la cabecera "Persona".');
      return;
    }
    const rows = this.personnel.filter(person => this.selectedPersonIds.has(person.id));
    this.promptAndApplyPatternRepeat(patternId, rows);
  }

  /** Botón "X" sobre una celda con turno/patrón asignado: la deja "Sin turno" sin tener que
   * usar "Limpiar" (que borra toda la semana). */
  clearCellAssignment(row: BulkPersonRow, day: WeekDay): void {
    if (!this.canSave || this.isCellDisabled(row, day.date) || this.isCellFromRotation(row, day)) { return; }
    this.personnel = this.personnel.map(person =>
      person.id === row.id ? clearRowDay(person, day.dayNumber) : person);
    this.hasLocalDraftChanges = true;
    this.save();
  }

  onDropRow(event: DragEvent, row: BulkPersonRow): void {
    event.preventDefault();
    const patternId = this.resolveDraggedPatternId(event);
    this.dragOverTarget = null;
    if (patternId && this.canSave) {
      const rows = this.selectedPersonIds.size > 0
        ? this.personnel.filter(person => this.selectedPersonIds.has(person.id))
        : [row];
      this.promptAndApplyPatternRepeat(patternId, rows);
    }
  }

  onDropCell(event: DragEvent, row: BulkPersonRow, day: WeekDay): void {
    event.preventDefault();
    const patternId = this.resolveDraggedPatternId(event);
    this.dragOverTarget = null;
    if (patternId && this.canSave) {
      if (this.selectedPersonIds.size > 0) {
        this.applyPatternToColumn(day, patternId);
      } else {
        this.applyPatternToCell(row, day, patternId);
      }
    }
  }

  onDropColumn(event: DragEvent, day: WeekDay): void {
    event.preventDefault();
    const patternId = this.resolveDraggedPatternId(event);
    this.dragOverTarget = null;
    if (patternId && this.canSave) { this.applyPatternToColumn(day, patternId); }
  }

  onTapRow(row: BulkPersonRow): void {
    if (!this.tapArmedPatternId || !this.canSave) { return; }
    const rows = this.selectedPersonIds.size > 0
      ? this.personnel.filter(person => this.selectedPersonIds.has(person.id))
      : [row];
    this.promptAndApplyPatternRepeat(this.tapArmedPatternId, rows);
  }

  onTapCell(row: BulkPersonRow, day: WeekDay): void {
    if (!this.tapArmedPatternId || !this.canSave) { return; }
    if (this.selectedPersonIds.size > 0) {
      this.applyPatternToColumn(day, this.tapArmedPatternId);
    } else {
      this.applyPatternToCell(row, day, this.tapArmedPatternId);
    }
  }

  onTapColumn(day: WeekDay): void {
    if (!this.tapArmedPatternId || !this.canSave) { return; }
    this.applyPatternToColumn(day, this.tapArmedPatternId);
  }

  /** "limpiar" usa la misma lógica de guardado pero enviando los días de la semana con workShiftId en null,
   * para asegurar que el backend solo limpie la semana actual y no todas las demás semanas programadas. */
  clearWeek(): void {
    if (!this.canSave || this.saving) { return; }
    const range = this.editableWeekRange();
    if (!range) {
      this.snackBService.openWarning('Toda esta semana está bloqueada; no hay nada que limpiar.');
      return;
    }

    if (this.personnel.length === 0) { return; }

    this.dialogService.openDialogConfirm(
      '¿Estás seguro de que deseas limpiar la programación de esta semana? Esta acción eliminará los turnos asignados a todas las personas en la semana mostrada.',
      true, true, 'Cancelar'
    ).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      
      const people = this.personnel.map(row => ({
        personalEmployerId: Number(row.id),
        days: this.weekDays
          .filter(d => !this.isCellDisabled(row, d.date))
          .map(d => ({ date: dateKey(d.date), workShiftId: null }))
      }));
      
      this.runSave(range.from, range.to, people);
    });
  }

  dismissSaveResult(): void {
    this.saveResult = null;
    this.rejectedCellReasons = new Map();
  }

  save(): void {
    if (!this.canSave || this.saving) { return; }
    if (!this.hasLocalDraftChanges) {
      this.snackBService.openWarning('No hay cambios sin guardar en esta semana.');
      return;
    }
    const range = this.editableWeekRange();
    if (!range) {
      this.snackBService.openWarning('Toda esta semana está bloqueada; no hay nada que guardar.');
      return;
    }
    const people = this.personnel.map(row => ({
      personalEmployerId: Number(row.id),
      days: this.weekDays
        .filter(d => !this.isCellDisabled(row, d.date))
        .map(d => {
          const assignment = row.assignments[d.dayNumber];
          // Un día heredado de la rotación se manda sin turno individual: enviarlo con su WorkShiftId
          // lo "congelaría" como ajuste y dejaría de seguir el patrón (vuelve a la rotación).
          const individualId = assignment && assignment.source !== 'ROTATION' ? assignment.patternId : null;
          return { date: dateKey(d.date), workShiftId: individualId };
        })
    }));
    this.runSave(range.from, range.to, people);
  }

  private runSave(
    from: string,
    to: string,
    people: Array<{ personalEmployerId: number; days: Array<{ date: string; workShiftId: string | null }> }>
  ): void {
    this.runBulkRequest(this.dataService.saveScheduleBulk(this.data.projectId, from, to, people));
  }

  /** Maneja el resultado de cualquier POST a Schedule/Bulk (guardar o limpiar) — mismo resumen
   * por persona, mismo toast, mismo recargue de la grilla. */
  private runBulkRequest(request$: Observable<BulkSaveResultApi>): void {
    this.saving = true;
    this.rejectedCellReasons = new Map();
    request$.subscribe({
      next: (result) => {
        this.saving = false;
        this.saveResult = result;
        this.rejectedCellReasons = this.buildRejectedCellReasons(result);
        const summary = `Guardado: ${result.Applied} completo(s), ${result.Partial} parcial(es), ${result.Skipped} sin cambios.`;
        if (result.Partial > 0 || result.Skipped > 0 || result.WithWarnings > 0) {
          this.snackBService.openWarning(summary);
        } else {
          this.snackBService.openSuccess(summary);
        }
        this.reloadPersonnel();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.snackBService.openError(this.extractErrorMessage(err));
      }
    });
  }

  /** Arrastrar/tocar un patrón sobre una persona o sobre la cabecera "Persona" (varias
   * seleccionadas) pregunta cuántas semanas repetirlo — a diferencia de soltar sobre una celda o
   * una columna, que sigue siendo instantáneo porque ahí solo se asigna un día suelto. */
  private promptAndApplyPatternRepeat(patternId: string, rows: BulkPersonRow[]): void {
    const pattern = this.getPatternById(patternId);
    if (!pattern || rows.length === 0) { return; }

    const dialogData: DialogRepeatPatternData = {
      patternName: pattern.name,
      patternColor: pattern.color,
      days: pattern.days.map(day => ({ letter: day.letter, color: day.color || pattern.color })),
      weekStartDate: this.weekStartDate,
      weekRangeLabel: this.weekRangeLabel
    };

    this.dialogService.openDialogRepeatPattern(dialogData).afterClosed().subscribe((weeks: number | false) => {
      if (!weeks) { return; }
      this.applyPatternForWeeks(pattern, rows, weeks);
    });
  }

  private buildWeekSpanDates(weeks: number): Date[] {
    const dates: Date[] = [];
    for (let offset = 0; offset < weeks * 7; offset++) {
      const date = new Date(this.weekStartDate);
      date.setDate(date.getDate() + offset);
      dates.push(date);
    }
    return dates;
  }

  /** "Aplicar" del diálogo de repetición guarda de una vez en el backend — a diferencia del
   * resto de la edición (borrador hasta que se pulsa "Guardar"), porque las semanas siguientes a
   * la visible no están cargadas en pantalla y no hay forma de dejarlas "pendientes". */
  private applyPatternForWeeks(pattern: ShiftPattern, rows: BulkPersonRow[], weeks: number): void {
    const spanDates = this.buildWeekSpanDates(weeks);
    const rangeStart = spanDates[0];
    const rangeEnd = spanDates[spanDates.length - 1];
    const editableFrom = this.effectiveEditableFrom();
    const effectiveFrom = editableFrom && editableFrom > rangeStart ? editableFrom : rangeStart;
    if (effectiveFrom > rangeEnd) {
      this.snackBService.openWarning('Todo el rango seleccionado está bloqueado; no hay nada que aplicar.');
      return;
    }

    const people = rows
      .map(row => ({
        personalEmployerId: Number(row.id),
        days: spanDates
          .filter(date => date >= effectiveFrom && !this.isOutOfContract(row, date))
          .map(date => ({ date: dateKey(date), workShiftId: pattern.id }))
      }))
      .filter(person => person.days.length > 0);

    if (people.length === 0) {
      this.snackBService.openWarning('Ninguna de las personas seleccionadas tiene días editables en ese rango.');
      return;
    }

    this.runSave(dateKey(effectiveFrom), dateKey(rangeEnd), people);
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
